import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, TrendingUp, Shield, Bell, BarChart3, Lock, ArrowRight,
  FileText, Calculator, CreditCard, Clock, RefreshCw, Briefcase,
  Globe, AlertCircle, PieChart, UserCheck, Archive, Layers, Check,
  Send, Landmark, BookOpen, Percent, UserPlus, CalendarCheck,
  FolderLock, GitBranch, Mail, LayoutDashboard, ChevronDown
} from 'lucide-react';
import '../styles/Services.css';
import { ServicesHero } from '../components/PageHeroes';

const Services = () => {
  const services = [
    { icon: Calculator, title: 'RTI Payroll Processing', description: 'Full Real Time Information (RTI) payroll submissions to HMRC on every pay run — weekly, fortnightly, or monthly.', category: 'Payroll Processing' },
    { icon: FileText, title: 'PAYE Management', description: 'End-to-end Pay As You Earn administration including tax codes, income tax, and National Insurance calculations.', category: 'Payroll Processing' },
    { icon: Users, title: 'Multi-User Payroll', description: 'Manage payroll for multiple employees with role-based access control and team collaboration tools.', category: 'Payroll Processing' },
    { icon: UserCheck, title: 'CIS Contractor Payroll', description: 'Construction Industry Scheme (CIS) deductions, monthly returns, and subcontractor verification built in.', category: 'Payroll Processing' },
    { icon: Globe, title: 'Umbrella Company Payroll', description: 'Compliant umbrella payroll for contractors and freelancers with full IR35 consideration and margin transparency.', category: 'Payroll Processing' },
    { icon: Layers, title: 'Multiple Pay Frequencies', description: 'Support for weekly, bi-weekly, four-weekly, and monthly pay runs across different employee groups simultaneously.', category: 'Payroll Processing' },

    { icon: Briefcase, title: 'Auto Enrolment & Pensions', description: 'Workplace pension auto enrolment compliance with support for NEST, Peoples Pension, and all major UK providers.', category: 'Compliance & Statutory' },
    { icon: CreditCard, title: 'Statutory Payments', description: 'Accurate calculation and processing of SSP, SMP, SPP, ShPP, and SAP in line with current HMRC rates.', category: 'Compliance & Statutory' },
    { icon: Clock, title: 'P11D & Benefits in Kind', description: 'Automated P11D preparation and submission for employee expenses and benefits, including Class 1A NIC calculations.', category: 'Compliance & Statutory' },
    { icon: RefreshCw, title: 'Year-End Processing', description: 'Stress-free P60 generation, P45 issuance, and full year-end reconciliation submitted directly to HMRC.', category: 'Compliance & Statutory' },
    { icon: Shield, title: 'Compliance Management', description: 'Stay compliant with HMRC RTI and GDPR regulations automatically with real-time legislative updates.', category: 'Compliance & Statutory' },

    { icon: Send, title: 'Invoicing & Quotes', description: 'Create, issue, and track customer invoices and quotes, with credit notes, payment recording, and automatic status tracking.', category: 'Finance & Accounts' },
    { icon: Landmark, title: 'Accounts Payable', description: 'Manage supplier bills and employee expense claims from approval through to payment, with full audit history.', category: 'Finance & Accounts' },
    { icon: CreditCard, title: 'Bank Reconciliation', description: 'Connect bank accounts, categorise transactions, and reconcile statements against your books.', category: 'Finance & Accounts' },
    { icon: BookOpen, title: 'General Ledger & Journals', description: 'Full double-entry accounting with a customisable chart of accounts, manual and recurring journals, and a live trial balance.', category: 'Finance & Accounts' },
    { icon: Percent, title: 'VAT Returns', description: 'UK 9-box VAT filing alongside generic VAT and sales-tax handling for organisations trading outside the UK.', category: 'Finance & Accounts' },

    { icon: UserPlus, title: 'Recruitment & Onboarding', description: 'Manage vacancies, candidates, and interviews through to offer, with structured onboarding checklists for every new hire.', category: 'Workforce & HR' },
    { icon: CalendarCheck, title: 'Leave Management', description: 'Employee leave requests, manager approvals, and balance tracking, with optional routing through custom approval workflows.', category: 'Workforce & HR' },
    { icon: Users, title: 'Employee Self-Service', description: 'Employees view their own payslips, submit expense claims, and manage leave requests without going through HR for every step.', category: 'Workforce & HR' },

    { icon: CalendarCheck, title: 'Appointment Scheduling', description: 'Bookable services, providers, and locations, with automatic confirmation emails and reminders sent ahead of each appointment.', category: 'Business Operations' },
    { icon: FolderLock, title: 'Document Management', description: 'Secure document storage with version history, access control, and sign-off tracking for contracts and records.', category: 'Business Operations' },
    { icon: GitBranch, title: 'Approval Workflows', description: 'Configurable multi-stage approval routing for bills, invoices, journals, and other records, with a full decision history.', category: 'Business Operations' },
    { icon: Mail, title: 'Email Marketing', description: 'Manage subscriber lists and send real email campaigns to customers and contacts, without a separate marketing tool.', category: 'Business Operations' },

    { icon: TrendingUp, title: 'Real-Time Analytics', description: 'Comprehensive dashboards and live payroll reports for data-driven decisions and workforce cost visibility.', category: 'Reporting & Insights' },
    { icon: LayoutDashboard, title: 'Financial Reporting', description: 'Live profit & loss, balance sheet, and cash position, plus AR and AP aging, computed directly from your ledger.', category: 'Reporting & Insights' },
    { icon: BarChart3, title: 'Advanced Reporting', description: 'Export detailed payroll journals, cost centre reports, and employee breakdowns to CSV and PDF instantly.', category: 'Reporting & Insights' },
    { icon: PieChart, title: 'Payroll Journal Integration', description: 'Seamless export of payroll journals to Xero, Sage, QuickBooks, and other major UK accounting platforms.', category: 'Reporting & Insights' },
    { icon: AlertCircle, title: 'Smart Reminders', description: 'Automated deadline alerts for FPS, EPS, P32, and pension contribution submissions so nothing is missed.', category: 'Reporting & Insights' },

    { icon: Archive, title: 'Payslip Management', description: 'Branded digital payslips delivered securely to employees via email or self-service portal with full history.', category: 'Support & Security' },
    { icon: Lock, title: 'Enterprise Security', description: 'ISO 27001 certified infrastructure with bank-level encryption, audit trails, and GDPR-compliant data handling.', category: 'Support & Security' },
    { icon: Bell, title: 'Dedicated Support', description: 'UK-based experts available via phone, email, and live chat to help with every query, submission, and setup question.', category: 'Support & Security' }
  ];

  const categories = [
    { name: 'Payroll Processing', icon: Calculator, blurb: 'RTI, PAYE, CIS, umbrella, and multi-frequency payroll runs' },
    { name: 'Compliance & Statutory', icon: Shield, blurb: 'Pensions, statutory payments, P11D, and year-end filing' },
    { name: 'Finance & Accounts', icon: BookOpen, blurb: 'Invoicing, accounts payable, banking, ledger, and VAT' },
    { name: 'Workforce & HR', icon: Users, blurb: 'Recruitment, onboarding, leave, and employee self-service' },
    { name: 'Business Operations', icon: GitBranch, blurb: 'Appointments, documents, approval workflows, and marketing' },
    { name: 'Reporting & Insights', icon: BarChart3, blurb: 'Live financial reporting, analytics, and smart reminders' },
    { name: 'Support & Security', icon: Lock, blurb: 'Payslips, enterprise security, and dedicated support' }
  ];

  // First category open by default so the page never looks empty on load
  const [openCategory, setOpenCategory] = useState(categories[0].name);

  const toggleCategory = (name) => {
    setOpenCategory((prev) => (prev === name ? null : name));
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
  };

  return (
    <motion.div
      className="services-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <ServicesHero />

      {/* Services — collapsible by category */}
      <section className="services-grid-section">
        <div className="services-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>Everything included, organised your way</h2>
            <p>Tap a category to expand it — seven branches of the platform, one service at a time.</p>
          </motion.div>

          <div className="services-accordion">
            {categories.map((category, catIdx) => {
              const CatIcon = category.icon;
              const isOpen = openCategory === category.name;
              const categoryServices = services.filter((s) => s.category === category.name);

              return (
                <div className={`service-accordion-item ${isOpen ? 'is-open' : ''}`} key={category.name}>
                  <button
                    className="service-accordion-header"
                    onClick={() => toggleCategory(category.name)}
                    aria-expanded={isOpen}
                  >
                    <span className="service-accordion-index">{String(catIdx + 1).padStart(2, '0')}</span>
                    <span className="service-accordion-icon">
                      <CatIcon size={22} />
                    </span>
                    <span className="service-accordion-titles">
                      <span className="service-accordion-title">{category.name}</span>
                      <span className="service-accordion-blurb">{category.blurb}</span>
                    </span>
                    <span className="service-accordion-count">{categoryServices.length} services</span>
                    <motion.span
                      className="service-accordion-chevron"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <ChevronDown size={20} />
                    </motion.span>
                  </button>

                  <motion.div
                    className="service-accordion-panel"
                    initial={false}
                    animate={isOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <motion.div
                      className="services-grid"
                      initial="hidden"
                      animate={isOpen ? 'visible' : 'hidden'}
                      variants={{
                        hidden: { opacity: 0 },
                        visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
                      }}
                    >
                      {categoryServices.map((service, idx) => {
                        const Icon = service.icon;
                        return (
                          <motion.div
                            key={idx}
                            className="service-card"
                            variants={itemVariants}
                          >
                            <div className="service-icon-box">
                              <Icon size={22} />
                            </div>
                            <h3>{service.title}</h3>
                            <p>{service.description}</p>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="services-features">
        <div className="services-container">
          <motion.div
            className="features-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="section-eyebrow">Why Effective Global Staff</p>
            <h2>Why Choose Effective Global Staff?</h2>
            <p className="features-subtitle">One platform, industry-leading on every part of it</p>
          </motion.div>

          <div className="features-list">
            {[
              'Lightning-fast payroll processing and RTI filing',
              '99.9% uptime guarantee',
              'Bank-level security & ISO 27001 certified',
              'Instant UK-based support & guidance',
              'HMRC & GDPR compliant',
              'Full auto enrolment pension support',
              'CIS and umbrella payroll ready',
              'Invoicing, accounts payable, and banking in one place',
              'General ledger, VAT returns, and live financial reporting',
              'Approval workflows, document sign-off, and appointment scheduling',
              'Integrates with Xero, Sage & QuickBooks',
              'Affordable pricing for all business sizes',
              'P11D, P60 and P45 automation included'
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                className="feature-item"
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.04 }}
              >
                <Check size={16} className="feature-check-icon" />
                <span>{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="services-cta-bg"></div>
        <div className="services-container">
          <motion.div
            className="services-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Transform How You Run Your Business</h2>
            <p>Experience seamless, secure, and compliant payroll, finance, and operations — all in one place</p>
            <Link to="/demo" className="btn btn-secondary">
              Request Demo <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Services;